import React, { useMemo, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { useLanguage } from '../../../../contexts/LanguageContext';
import useTrainingReportParticipantsData from '../hooks/useTrainingReportParticipantsData';
import DealerDropdownBySap from '../../../../components/common/DealerDropdownBySap';

/**
 * Rapor detayındaki katılımcı satırları. `kunnr` opsiyoneldir; bayi seçilirse SAP kodu
 * (`erpDealerNumber`) gönderilir, backend 10 haneye tamamlayarak saklar (doc §5.2).
 * Karsan içi / bayi dışı katılımcı için bayi seçilmeden de eklenebilir.
 * Bkz. docs/LMS-Egitim-Raporlari-API.md §5.
 */
const TrainingReportParticipantsTab = ({ trainingReportId, readOnly = false }) => {
    const { th } = useLanguage();
    const { participantsApi, isLoading, isAdding, isDeleting, addParticipant, removeParticipant } = useTrainingReportParticipantsData(trainingReportId);

    const [participantName, setParticipantName] = useState('');
    const [selectedDealer, setSelectedDealer] = useState(null);

    const participants = useMemo(() => participantsApi?.items || [], [participantsApi]);

    const handleAdd = async () => {
        if (!participantName || !participantName.trim()) return;
        const kunnr = selectedDealer?.erpDealerNumber || null;
        const success = await addParticipant({ participantName: participantName.trim(), kunnr });
        if (success) {
            setParticipantName('');
            setSelectedDealer(null);
        }
    };

    const handleRemove = (row) => {
        confirmDialog({
            message: th('trainingReport.participants.confirmDeleteMessage'),
            header: th('common.delete'),
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: () => removeParticipant(row.id)
        });
    };

    const actionBodyTemplate = (row) => <i className="pi pi-trash cursor-pointer text-red-500 hover:text-red-700" style={{ fontSize: '1.1rem' }} title={th('common.delete')} onClick={() => handleRemove(row)} />;

    return (
        <div>
            <ConfirmDialog />
            {!readOnly && (
                <div className="flex flex-wrap align-items-end gap-2 mb-3">
                    <div style={{ minWidth: '16rem' }}>
                        <label className="block mb-1">
                            {th('trainingReport.participants.fields.participantName')} <span className="text-red-500">*</span>
                        </label>
                        <InputText value={participantName} maxLength={500} onChange={(e) => setParticipantName(e.target.value)} className="w-full" />
                    </div>
                    <div style={{ minWidth: '18rem' }}>
                        <label className="block mb-1">{th('trainingReport.participants.fields.dealer')}</label>
                        <DealerDropdownBySap value={selectedDealer} onChange={setSelectedDealer} placeholder={th('trainingReport.participants.fields.selectDealerPlaceholder')} className="w-full" />
                    </div>
                    <Button label={th('trainingReport.participants.addButton')} icon="pi pi-plus" onClick={handleAdd} disabled={!participantName || !participantName.trim()} loading={isAdding} />
                </div>
            )}

            <DataTable value={participants} loading={isLoading || isDeleting} dataKey="id" emptyMessage={th('trainingReport.participants.emptyMessage')} className="p-datatable-gridlines" showGridlines>
                {!readOnly && <Column body={actionBodyTemplate} style={{ width: '4rem' }} align="center" />}
                <Column field="participantName" header={th('trainingReport.participants.columnParticipantName')} />
                <Column field="dealerName" header={th('trainingReport.participants.columnDealer')} />
                <Column field="kunnr" header={th('trainingReport.participants.columnDealerCode')} style={{ width: '10rem' }} />
            </DataTable>
        </div>
    );
};

export default TrainingReportParticipantsTab;
